interface Pt { x: number; y: number; i: number }

function ring(data: number[], start: number, end: number, dim: number): Pt[] {
    const out: Pt[] = [];
    for (let i = start; i < end; i += dim) {
        out.push({ x: data[i], y: data[i + 1], i: i / dim });
    }
    return out;
}

/** Twice the signed area. Positive is counter-clockwise. */
function signedArea2(pts: Pt[]): number {
    let sum = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        sum += pts[j].x * pts[i].y - pts[i].x * pts[j].y;
    }
    return sum;
}

function cross(a: Pt, b: Pt, c: Pt): number {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** Inside or on the edge of the CCW triangle abc. */
function pointInTriangle(a: Pt, b: Pt, c: Pt, p: Pt): boolean {
    return cross(a, b, p) >= 0 && cross(b, c, p) >= 0 && cross(c, a, p) >= 0;
}

function onSegment(a: Pt, b: Pt, p: Pt): boolean {
    return Math.min(a.x, b.x) <= p.x && p.x <= Math.max(a.x, b.x)
        && Math.min(a.y, b.y) <= p.y && p.y <= Math.max(a.y, b.y);
}

/** Proper segment intersection — shared endpoints do not count. */
function segmentsCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
    const d1 = cross(c, d, a), d2 = cross(c, d, b);
    const d3 = cross(a, b, c), d4 = cross(a, b, d);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0))
        && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
    if (d1 === 0 && onSegment(c, d, a)) return true;
    if (d2 === 0 && onSegment(c, d, b)) return true;
    if (d3 === 0 && onSegment(a, b, c)) return true;
    if (d4 === 0 && onSegment(a, b, d)) return true;
    return false;
}

/** True when segment m→p crosses any edge of any ring, endpoints excepted. */
function blocked(m: Pt, p: Pt, rings: Pt[][]): boolean {
    for (const r of rings) {
        for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
            const a = r[j], b = r[i];
            if (a === m || a === p || b === m || b === p) continue;
            if (segmentsCross(m, p, a, b)) return true;
        }
    }
    return false;
}

/**
 * Splice one hole into the outer ring with a two-way bridge.
 *
 * The hole's rightmost vertex M is joined to the nearest outer vertex it can
 * see; both endpoints are duplicated so the merged ring stays a single closed
 * loop that ear clipping can consume.
 */
function bridgeHole(outer: Pt[], hole: Pt[], others: Pt[][]): Pt[] {
    let mIdx = 0;
    for (let i = 1; i < hole.length; i++) if (hole[i].x > hole[mIdx].x) mIdx = i;
    const m = hole[mIdx];

    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < outer.length; i++) {
        const p = outer[i];
        const dist = (p.x - m.x) ** 2 + (p.y - m.y) ** 2;
        if (dist >= bestDist) continue;
        if (blocked(m, p, [outer, hole, ...others])) continue;
        best = i;
        bestDist = dist;
    }
    // No visible vertex (self-intersecting input): drop the hole rather than
    // emit garbage. The caller still gets a solid outer face.
    if (best < 0) return outer;

    const rotated = [...hole.slice(mIdx), ...hole.slice(0, mIdx)];
    return [
        ...outer.slice(0, best + 1),
        ...rotated,
        rotated[0],
        outer[best],
        ...outer.slice(best + 1),
    ];
}

export function triangulate(data: number[], holeIndices?: number[] | null, dim = 2): number[] {
    const holes = holeIndices ?? [];
    const outerEnd = holes.length ? holes[0] * dim : data.length;
    let outer = ring(data, 0, outerEnd, dim);
    if (outer.length < 3) return [];
    if (signedArea2(outer) < 0) outer.reverse();

    const holeRings: Pt[][] = [];
    for (let h = 0; h < holes.length; h++) {
        const start = holes[h] * dim;
        const end = h + 1 < holes.length ? holes[h + 1] * dim : data.length;
        const r = ring(data, start, end, dim);
        if (r.length < 3) continue;
        // Holes wound opposite to the outer ring, so the bridged loop stays
        // consistently oriented.
        if (signedArea2(r) > 0) r.reverse();
        holeRings.push(r);
    }
    // Rightmost hole first: an earlier bridge can never block a later one on
    // its right, which is what makes the greedy visibility search terminate.
    holeRings.sort((a, b) => Math.max(...b.map(p => p.x)) - Math.max(...a.map(p => p.x)));
    for (let i = 0; i < holeRings.length; i++) {
        outer = bridgeHole(outer, holeRings[i], holeRings.slice(i + 1));
    }

    return earClip(outer);
}

function earClip(poly: Pt[]): number[] {
    const out: number[] = [];
    const verts = [...poly];
    let guard = verts.length * verts.length + 16;

    while (verts.length > 3 && guard-- > 0) {
        let clipped = false;
        for (let i = 0; i < verts.length; i++) {
            const a = verts[(i - 1 + verts.length) % verts.length];
            const b = verts[i];
            const c = verts[(i + 1) % verts.length];
            if (cross(a, b, c) <= 0) continue; // reflex or collinear
            let contains = false;
            for (const p of verts) {
                if (p === a || p === b || p === c) continue;
                if (pointInTriangle(a, b, c, p)) { contains = true; break; }
            }
            if (contains) continue;
            out.push(a.i, b.i, c.i);
            verts.splice(i, 1);
            clipped = true;
            break;
        }
        if (!clipped) {
            // Degenerate or self-intersecting remainder: fan it and stop
            // guessing. A slightly wrong tessellation beats an infinite loop.
            for (let i = 1; i < verts.length - 1; i++) out.push(verts[0].i, verts[i].i, verts[i + 1].i);
            return out;
        }
    }
    if (verts.length === 3) out.push(verts[0].i, verts[1].i, verts[2].i);
    return out;
}
